import { useEffect, useRef, useState } from 'react';
import { MapView } from '../map/MapView';
import { CardRail } from '../round/CardRail';
import { HiderHand } from '../round/HiderHand';
import {
  EXPLAINER_BEATS,
  EXPLAINER_DURATION_MS,
  EXPLAINER_STILL,
  explainerHiderCard,
  explainerSeekerCard,
  explainerState,
  type ExplainerBeat,
} from '../../shared/explainer';

/**
 * v28 BOLT-156 — the scripted round on the Home page.
 *
 * Jake: "an actual code based scripted game play on the home page… The whole
 * thing could be ~ 10-20 seconds and be very effective at explaining the general
 * concept." And, explicitly, NOT a recorded GIF.
 *
 * THE WHOLE VALUE IS THAT IT IS THE REAL THING. `MapView` is the map the round
 * plays on; `CardRail` and `HiderHand` are the rails the round plays with; the
 * zones come from the resolver the SERVER runs. Nothing here is a recreation,
 * so nothing here can teach a game we no longer ship.
 *
 * It cannot start a game or reach session state: it takes no props, holds no
 * socket, calls no `send`, and every frame it renders is built from a constant.
 */

/** Reduced motion ⇒ the last frame, statically. The outcome, not nothing. */
function prefersReduced(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * How long Home paints before the demo's map is built.
 *
 * A WebGL context plus a style plus tiles is the most expensive thing on this
 * screen, and it competes with the socket connect that gates "Create a game" -
 * the actual reason most people are here. Deferring it makes the primary action
 * available SOONER, and a player who taps Create inside a second never pays for
 * a map they did not stay to watch.
 *
 * It also stopped the CI gate blowing its 40 minute budget: every spec loads
 * Home twice, and on a runner without a GPU each of those contexts is real
 * money. The delay is not for the tests, but they benefit for the same reason
 * a phone does.
 */
const MAP_DELAY_MS = 800;

export function Explainer() {
  const [beat, setBeat] = useState<ExplainerBeat>(EXPLAINER_BEATS[0]);
  const [reduced, setReduced] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const holder = useRef<HTMLDivElement>(null);
  const visible = useRef(true);

  useEffect(() => {
    const t = setTimeout(() => setMapReady(true), MAP_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (prefersReduced()) {
      setReduced(true);
      setBeat(EXPLAINER_STILL);
      return;
    }
    // Off-screen costs nothing: the Home page is also where a player waits for
    // the socket, and a looping map behind the fold is pure battery.
    const io =
      typeof IntersectionObserver === 'function' && holder.current
        ? new IntersectionObserver(([e]) => (visible.current = e.isIntersecting), { threshold: 0.15 })
        : null;
    if (io && holder.current) io.observe(holder.current);

    const started = Date.now();
    let i = 0;
    const tick = setInterval(() => {
      if (!visible.current) return;
      const t = (Date.now() - started) % EXPLAINER_DURATION_MS;
      let next = 0;
      for (let k = 0; k < EXPLAINER_BEATS.length; k++) if (t >= EXPLAINER_BEATS[k].atMs) next = k;
      if (next === i) return;
      i = next;
      setBeat(EXPLAINER_BEATS[next]);
    }, 100);
    return () => {
      clearInterval(tick);
      io?.disconnect();
    };
  }, []);

  const seekerCard = explainerSeekerCard(beat);
  const hiderCard = explainerHiderCard(beat);

  return (
    <div
      className="explainer"
      data-testid="explainer"
      data-beat={beat.id}
      data-reduced={reduced ? 'true' : 'false'}
      data-map={mapReady ? 'on' : 'pending'}
      ref={holder}
      aria-label="A short example round"
    >
      <div className="explainer__map">
        {/* The SHIPPED renderer, driven by a snapshot. `interactive={false}`
            because this is a poster, not a map: a Home page that pans under a
            thumb is a Home page nobody can scroll. */}
        {mapReady && <MapView state={explainerState(beat)} interactive={false} fitOnFirstBoundary={false} refitBoundary />}
      </div>

      {/* Jake: "the cards … could be shown on the right when the seeker plays a
          card, and shown on the left when a hider plays a card." */}
      <div className="explainer__card explainer__card--right" data-testid="explainer-seeker-card" data-shown={!!seekerCard}>
        {seekerCard && <CardRail cards={[seekerCard]} onPick={() => {}} />}
      </div>
      <div className="explainer__card explainer__card--left" data-testid="explainer-hider-card" data-shown={!!hiderCard}>
        {hiderCard && <HiderHand cards={[hiderCard]} onPick={() => {}} discardMode={false} />}
      </div>

      <div className="explainer__caption" data-testid="explainer-caption">
        {beat.caption}
      </div>
    </div>
  );
}
